/**
 * The run verdict with its criteria rows and rule, for `VerdictChip`.
 *
 * ONE builder for the two places a clearance run is judged — the dock header
 * and the overlay card — so the card's chip and the dock's chip cannot list
 * different tests or quote different bands.
 */
import { useTranslation } from 'react-i18next';

import type { Verdict, VerdictRow } from '@/components/VerdictChip';
import { formatFixed } from '@/lib/stationVolumes/format';

import type { ClearanceSummary } from './types';
import { CLEARANCE_THRESHOLD_PCT, clearanceCriteria, clearanceVerdict, marginalBandM, pctDigits } from './verdict';

export interface ClearanceVerdictView {
  verdict: Verdict;
  rows: VerdictRow[];
  /** The rule in one sentence — the chip's footnote and tooltip. */
  rule: string;
  /** The MARGINAL band, formatted for copy (`0.35`). */
  band: string;
  /** The two shares, formatted for copy (`0.04 %`). */
  insidePct: string;
  bandPct: string;
}

export const useClearanceVerdict = (
  summary: Pick<ClearanceSummary, 'violationCount' | 'scoredPointCount' | 'nearMiss'>,
  nearMissBandsM: readonly number[] | null | undefined,
): ClearanceVerdictView => {
  const { t } = useTranslation('project');
  const band = formatFixed(marginalBandM(nearMissBandsM), 2);
  const input = {
    violationCount: summary.violationCount,
    scoredCount: summary.scoredPointCount,
    nearMiss: summary.nearMiss,
  };
  const verdict = clearanceVerdict(input);
  const criteria = clearanceCriteria(input);
  const pct = (v: number) => `${formatFixed(v, pctDigits(v))} %`;
  // One row per test the verdict applies, in the order it applies them, each
  // marked with the severity IT produces on its own threshold — the verdict is
  // the worse of the two. "Points scored" is the precondition, not a test:
  // something to judge or not. The chip draws it neutral (ⓘ) while it holds —
  // a green ✓ read as a passed check (audit A8) — and INCONCLUSIVE when it fails.
  const rows: VerdictRow[] = [
    {
      key: 'scored',
      kind: 'precondition',
      label: t('clearance.dock.verdictScored'),
      actual: summary.scoredPointCount.toLocaleString(),
      severity: summary.scoredPointCount > 0 ? 'PASS' : 'INCONCLUSIVE',
    },
    {
      key: 'inside',
      label: t('clearance.dock.verdictInside'),
      actual: `${summary.violationCount.toLocaleString()} (${pct(criteria.insidePct)})`,
      required: '0',
      severity: criteria.inside,
    },
    {
      key: 'nearMiss',
      label: t('clearance.dock.verdictWithin', { band }),
      actual: `${summary.nearMiss.within0_35M.toLocaleString()} (${pct(criteria.bandPct)})`,
      required: `< ${formatFixed(CLEARANCE_THRESHOLD_PCT.passBand, 0)} %`,
      severity: criteria.band,
    },
  ];
  return {
    verdict,
    rows,
    rule: t('clearance.dock.verdictRule', ruleVars(band)),
    band,
    insidePct: pct(criteria.insidePct),
    bandPct: pct(criteria.bandPct),
  };
};

/** The thresholds as the rule sentences quote them. */
export const ruleVars = (band: string) => ({
  band,
  failInside: formatFixed(CLEARANCE_THRESHOLD_PCT.failInside, 0),
  passBand: formatFixed(CLEARANCE_THRESHOLD_PCT.passBand, 0),
  marginalBand: formatFixed(CLEARANCE_THRESHOLD_PCT.marginalBand, 0),
});
